// Guided tours ("Show me around"): the registry. TourHost.tsx renders whichever tour is running; tourStore.ts holds
// startTour() and the per-user "seen" flags.
//
// Adding a tour:
//   1. Add an entry below: id (stable, it is part of the storage key), path (where startTour navigates to),
//      match (which urls the tour belongs to), the help-panel title / meta / length, and its steps.
//   2. Put data-tour="<step.target>" on each element a step highlights. Prefer the whole card/section, not a child.
//   3. That's it: the tour auto-starts the first time a user opens a matching page (once its first target exists),
//      and the help panel lists it under "Show me around".
// A step whose target is missing (still loading, empty, hidden at this width) shows as a centred callout instead.
// placement = the side of the target the callout prefers (Figma frame); it flips or docks when there is no room.

import Stock from '../../../model/Stock';
import { loadRegisterOrder } from '../shareRegistry/HolderDetail';

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TourStep {
    target: string; // data-tour value
    title: string;
    body: string;
    placement: TourPlacement;
    tip?: string; // optional extra line under the body
    points?: string[]; // optional bullets between the body and the tip (Website Hub tours)
}

export interface Tour {
    id: string;
    path: string;
    match: (pathname: string) => boolean;
    title: string; // help panel: "Show me around …"
    meta: string;
    length: string;
    label?: string; // step tag prefix, e.g. "ANALYTICS · STEP 1 OF 4"
    steps: TourStep[];
    // for a page whose url needs an id (holder detail): started off that page, TourHost asks this for a url to open;
    // null → it goes to `path` instead and shows `notFound`
    findPath?: (stock: Stock) => Promise<string | null>;
    notFound?: string;
}

// Figma 02 Dashboard – Tour step 1..6 (478:737 … 478:3287)
const DASHBOARD: Tour = {
    id: 'dashboard',
    path: '/dashboard/overview',
    match: p => p === '/' || p.startsWith('/dashboard/overview'),
    title: 'Show me around the Dashboard',
    meta: 'What each part of the home page does',
    length: '6 steps · 2 min',
    steps: [
        {
            target: 'dashboard-attention', placement: 'right', title: 'Start here every morning',
            body: '“Needs your attention” lists the things only you can do — approve an announcement email, answer an investor question, publish a draft. When this list is empty, you\'re done for the day.',
        },
        {
            target: 'dashboard-quick-actions', placement: 'left', title: 'The four things you\'ll do most',
            body: 'Quick actions saves you hunting through menus. Add an announcement, send an email campaign, run a holdings report or answer investor questions — one click each.',
        },
        {
            target: 'dashboard-glance', placement: 'bottom', title: 'Your numbers at a glance',
            body: 'Shareholders, subscribed contacts and website visitors, with the change since last month. Click any number to open the detail behind it.',
        },
        {
            target: 'dashboard-share-price', placement: 'right', title: 'Your share price, always current',
            body: 'This comes straight from the ASX with a 20‑minute delay — you never type it in. The same figures appear on your website through the Investi widget.',
        },
        {
            // Figma names the right-hand card "Website Hub & email activity"; on the page it is "Latest from Investors"
            target: 'dashboard-activity', placement: 'top', title: 'Did the email go out? Did anyone read it?',
            body: '“Recent announcements” shows what was sent and how many investors opened it. “Latest from investors” shows what\'s happening on your website — questions, sign‑ups and messages.',
        },
        {
            target: 'primary-nav', placement: 'right', title: 'Where everything else lives',
            body: 'Four menus across the top: Dashboard, Share Registry, Communications and Website Hub. Whatever you need, you\'re never more than two clicks away. Forgotten something? Click the ? icon in the top bar and choose “Show me around” to run this tour again.',
        },
    ],
};

// Figma 02b Analytics – Tour step 1..4 (487:1757 … 487:3143)
const ANALYTICS: Tour = {
    id: 'analytics',
    path: '/dashboard/analytics',
    match: p => p.startsWith('/dashboard/analytics'),
    title: 'Show me around Analytics',
    meta: 'How to read the numbers and export a board pack',
    length: '4 steps · 90 sec',
    label: 'Analytics',
    steps: [
        {
            // Figma: "the last 90 days with the 90 days before it"; the page compares quarters
            target: 'analytics-key-results', placement: 'bottom', title: 'Six numbers, one quarter',
            body: 'Each tile compares this quarter with the one before it. Click a tile\'s link to open the detail behind the number — the registry, the campaigns or the announcements.',
        },
        {
            target: 'analytics-chart', placement: 'right', title: 'Did the market notice?',
            body: 'The navy line is how many shareholders you have; the cyan line is your share price. The gold markers are announcements. When the two lines rise together after a marker, that release brought new investors in.',
        },
        {
            target: 'analytics-announcements', placement: 'right', title: 'Which announcements landed',
            body: 'Views, email opens and the next‑day price move for every release. The most viewed are at the top — it\'s usually drilling results, not routine lodgements.',
        },
        {
            // Figma: "a four‑page PDF … set it to run every month"; the export has selectable sections and the monthly schedule is preview only
            target: 'analytics-export', placement: 'left', title: 'Turn all of this into a board pack',
            body: '“Export board report” builds a PDF with these numbers, the chart, the announcement table and suggested next steps — ready to send to your directors.',
        },
    ],
};

// ── Share Registry (Figma 05 Share Registry, page 449:5): one short tour per tab, plus the holder page ──
// ShareRegistry.tsx routes /share-registry/:tab?/:id?; each match takes only its own tab, so All holders doesn't also
// claim the subpages (and Holdings movement doesn't claim a day's report at /holdings-movement/{date}).
const registryPath = (p: string) => p.replace(/\/+$/, '');

// Figma Registry – Tour · All holders 1..5 (567:3052 … 567:5028), notes 567:5967 … 567:6027
const REGISTRY_ALL_HOLDERS: Tour = {
    id: 'registry-all-holders',
    path: '/share-registry',
    match: p => registryPath(p) === '/share-registry',
    title: 'Show me around the Share Registry',
    meta: 'The register, finding a holder and downloading the list',
    length: '5 steps · 2 min',
    label: 'All holders',
    steps: [
        {
            target: 'registry-tabs', placement: 'right', title: 'Four views of the same register',
            body: 'Everything about your shareholders lives here. All holders is the full list, Top holders is the twenty biggest, Holdings movement shows who bought and sold, and Reports makes the documents your board and the ASX ask for.',
        },
        {
            // Figma: "straight from Automic"; the provider differs per client
            target: 'registry-stats', placement: 'bottom', title: 'Your register, updated overnight',
            body: 'These numbers come straight from your share registry and refresh every night. You never type a shareholder in by hand — when the register changes, this changes with it.',
        },
        {
            // Figma: "a postcode" in the search and three chips; search covers name, holder number and email, and there is a State chip
            target: 'registry-filters', placement: 'bottom', title: 'Find anyone in seconds',
            body: 'Type a name, an email or a holder number in the search box. The chips beside it narrow your holders down to the group you want — by holder type, by whether you have their email, by how many shares they hold, or by state.',
        },
        {
            // Figma: "every email they have opened"; the holder page shows emails from the last 30 days
            target: 'registry-holder-row', placement: 'bottom', title: 'Click a name to see everything',
            body: 'Opening a holder shows their registry details and how their holding has changed since they first invested. If they have signed up on your Website Hub you also see the pages they have visited and the emails they have opened.',
        },
        {
            // Figma: "a PDF to print, an Excel file … or a CSV"; only the CSV is built (PDF and Excel are preview only)
            target: 'registry-download', placement: 'bottom', title: 'One button, your whole list',
            body: 'Download this list gives you a CSV of the holders you\'re looking at, ready for Excel or another system. Contact details are left out unless you tick them, because that file is sensitive.',
        },
    ],
};

// Figma Registry – Tour · Top holders 1..3 (579:3052, 579:3482, 579:3912), notes 579:8917 … 579:8947
const REGISTRY_TOP_HOLDERS: Tour = {
    id: 'registry-top-holders',
    path: '/share-registry/top-holders',
    match: p => registryPath(p) === '/share-registry/top-holders',
    title: 'Show me around Top holders',
    meta: 'Who owns the company and the 5% line',
    length: '3 steps · 1 min',
    label: 'Top holders',
    steps: [
        {
            target: 'top-holders-overview', placement: 'left', title: 'The twenty biggest holders',
            body: 'Your top holders in order, with the number of shares and the percentage of the company each one holds. This is the table your annual report and most board packs ask for.',
        },
        {
            // Figma: "Investi flags them here so you can check the notice arrived"; this table has no 5% flag (the holder page does)
            target: 'top-holders-list', placement: 'left', title: 'Watch the 5% line',
            body: 'Anyone at 5% or more is a substantial holder and must lodge a notice with the ASX — check the % Issued column for them. The 30‑day change column shows how much each holding moved in the last month.',
        },
        {
            target: 'top-holders-download', placement: 'bottom', title: 'Take it to the board',
            body: 'Download this list gives you the same page that sits in your monthly board pack — a PDF to print, or a spreadsheet if you want to work with the numbers.',
        },
    ],
};

// Figma Registry – Tour · Holdings movement 1..3 (579:4342, 579:4843, 579:5344), notes 579:8962 … 579:8992
const REGISTRY_HOLDINGS_MOVEMENT: Tour = {
    id: 'registry-holdings-movement',
    path: '/share-registry/holdings-movement',
    match: p => registryPath(p) === '/share-registry/holdings-movement',
    title: 'Show me around Holdings movement',
    meta: 'Running a movement report and reading it',
    length: '3 steps · 1 min',
    label: 'Holdings movement',
    steps: [
        {
            // Figma: "between two dates … last month, last quarter, or set your own"; a report compares one day with the trading day before
            target: 'movement-run', placement: 'bottom', title: 'Pick the date',
            body: 'Each movement report compares the register on one day with the trading day before. Choose a date and press Run Report — Investi shows you what changed, and the chart tracks how much moved day by day.',
        },
        {
            // Figma: "Green means the holding grew, red means it shrank"; the daily table marks holders with New / Existing / Returning badges
            target: 'movement-daily', placement: 'top', title: 'Who bought and who sold',
            body: 'One row per trading day. The badges mark new holders, existing holders who added to their position and holders returning after selling out, so a new investor building a position is easy to spot. Open a row for the full report.',
        },
        {
            // Figma: "the PDF or spreadsheet, and Email this report … every trading day at 8:00 am"; the button downloads a CSV, there is no email here
            target: 'movement-download', placement: 'bottom', title: 'Send it to the board',
            body: 'Download report gives you these daily figures as a spreadsheet, ready to send to your directors or your broker.',
        },
    ],
};

// Figma Registry – Tour · Reports 1..3 (579:5845, 579:6272, 579:6699), notes 579:9007 … 579:9037
const REGISTRY_REPORTS: Tour = {
    id: 'registry-reports',
    path: '/share-registry/reports',
    match: p => registryPath(p) === '/share-registry/reports',
    title: 'Show me around Registry reports',
    meta: 'The four ready-made reports and the board pack',
    length: '3 steps · 1 min',
    label: 'Reports',
    steps: [
        {
            target: 'reports-cards', placement: 'bottom', title: 'Four reports, one press each',
            body: 'These are the registry reports a listed company actually needs — the Top 20 and the holder bands for your annual report, who bought and sold for the board, and substantial holders for the ASX. Each card says what it is and why you need it.',
        },
        {
            // Figma describes a working monthly email; the switch is preview only (no setting or API yet)
            target: 'reports-cards', placement: 'bottom', title: 'Have them arrive by themselves',
            body: '‘Email it to me every month’ will make the report on the 1st and email it to you, so you never have to remember to run it. It\'s a preview for now — the switch doesn\'t send anything yet.',
        },
        {
            // Figma: "Everything you make is kept here for twelve months"; report history is sample rows until it is connected
            target: 'reports-pack', placement: 'bottom', title: 'The monthly board pack',
            body: 'One press builds all four reports into a single PDF with a written summary at the front — the version most directors actually read.',
        },
    ],
};

// Figma Registry – Tour · Holder detail 1..3 (593:4564, 593:5147, 593:5730), notes 579:9052 … 579:9082
// The url needs a holder id: started from Help elsewhere, it opens the largest holder (first in register order, the
// same list Previous / Next holder walks); with no register it goes to All holders and says why.
const REGISTRY_HOLDER: Tour = {
    id: 'registry-holder',
    path: '/share-registry',
    match: p => p.startsWith('/share-registry/holder/'),
    findPath: async stock => {
        const ids = await loadRegisterOrder(stock.id, stock.ticker, stock.exchange);
        return ids && ids.length ? `/share-registry/holder/${ids[0]}` : null;
    },
    notFound: 'No holders on the register yet — open a holder from the register to see this tour.',
    title: 'Show me around a holder',
    meta: 'Registry details, movements and website activity',
    length: '3 steps · 1 min',
    label: 'Holder detail',
    steps: [
        {
            // Figma: "straight from Automic … The green tick"; the provider differs per client and the email status is a Subscribed badge
            target: 'holder-details', placement: 'bottom', title: 'Everything the registry knows',
            body: 'Holder number, type, address, email and the date they first invested — straight from your share registry. A green ‘Subscribed’ badge tells you this person has agreed to receive your emails.',
        },
        {
            target: 'holder-movements', placement: 'bottom', title: 'How their holding changed',
            body: 'Every buy, sell and transfer since they first appeared on the register, with the price at the time, and a chart showing their holding against the share price.',
        },
        {
            // Figma: "every announcement email they opened … The list scrolls"; emails cover the last 30 days and the list doesn't scroll
            target: 'holder-activity', placement: 'top', title: 'What they do on your website',
            body: 'If this holder signed up on your Website Hub you also see every page they visited, the announcement emails they opened in the last 30 days and every question they asked.',
        },
    ],
};

// Website Hub tours (Figma 07 Website Hub, "v3 / Website Hub – … tour step n"). Each tour runs on one page, so the
// steps Figma draws on a subpage highlight that subpage's way in on this page (the library card, the Form Settings button).
const hubPage = (path: string) => (p: string) => p === path || p === `${path}/`;

// Overview tour step 1..2 (718:5521, 719:32779); Figma spotlights the whole page, here the card each step starts with
const WEBSITE_HUB: Tour = {
    id: 'website-hub',
    path: '/website-hub',
    match: hubPage('/website-hub'),
    title: 'Show me around the Website Hub',
    meta: 'What the Overview tells you at a glance',
    length: '2 steps · 1 min',
    steps: [
        {
            // Figma: "Every card is a shortcut"; the stat cards aren't links
            target: 'hub-overview-attention', placement: 'right', title: 'Everything your website shows',
            body: 'Website Hub is the content side of Investi — the pages, documents and people your website pulls from, plus the questions and messages that come back.',
            points: [
                '“Needs your attention” lists only the things a person has to do: unanswered questions, new form messages, widgets not yet added to the site.',
                'The four numbers along the top cover the last 30 days — visitors, page views, new sign‑ups, and how many widgets are live.',
                '“Recent activity” is a running log: a message arrives, someone signs up, an article is published, an answer goes out.',
                'Nothing on this page is a setting. Its buttons and links are shortcuts to the page that fixes it.',
            ],
            tip: 'Tip · A widget only counts as live once Investi can see it on the page, so a low number usually means the code has not been pasted in yet.',
        },
        {
            // Figma: each number "compared with the 30 days before", the chart "marks the days you released an announcement",
            // Full Analytics has "date ranges"; only visitors has a comparison, the chart marks its peak, Analytics goes by quarter
            target: 'hub-overview-traffic', placement: 'right', title: 'How the website is doing',
            body: 'Enough traffic detail to answer “did anyone read it?” without leaving Investi.',
            points: [
                'Visitors, page views and sign‑ups for the last 30 days, with visitors compared with the 30 days before.',
                'The chart marks the announcement behind the biggest peak, so you can see whether the news moved traffic.',
                '“Most visited pages” lists the pages investors actually open, and which Investi widget sits on each one.',
                '“Full Analytics” opens the complete report in the Dashboard, quarter by quarter, with visitor sources.',
            ],
            tip: 'Tip · Traffic to the investor pages usually rises for about 48 hours after a price‑sensitive announcement, then settles.',
        },
    ],
};

// Website content tour step 1..7 (718:5998, 718:6304, 718:31637, 718:32103, 718:32565, 718:32889, 718:33540).
// Figma draws steps 2–7 on each library's own page; here each step highlights that library's card.
const WEBSITE_CONTENT: Tour = {
    id: 'website-content',
    path: '/website-hub/content',
    match: hubPage('/website-hub/content'),
    title: 'Show me around Website content',
    meta: 'The six libraries your website widgets show',
    length: '7 steps · 3 min',
    steps: [
        {
            // Figma: Widget Settings "on any library jumps straight to that widget"; Other documents opens the widget list
            target: 'hub-content-sections', placement: 'bottom', title: 'One place per kind of content',
            body: 'Website content holds six libraries. Each one feeds a matching widget you paste into WordPress once.',
            points: [
                'News & media, Board & management, Corporate governance, Other documents, Company details and Capital structure.',
                'Every card shows how many items are live and when it was last updated, so you can see what has gone stale.',
                '“Widget Settings” on each library’s page opens that widget’s code and options.',
                'Open a library to add, edit, reorder or hide what visitors see.',
            ],
            tip: 'Tip · Add the content first and the widget second — the widget simply shows whatever is in the library when the page loads.',
        },
        {
            // Figma: "Four kinds of item live here"; the library has five (articles, videos, podcasts, webinars, coverage)
            target: 'hub-content-news-media', placement: 'right', title: 'News, video, podcasts and coverage',
            body: 'Articles, videos, podcasts, webinars and media coverage live here, and all of them become real posts on your WordPress site.',
            points: [
                'Articles are written in Investi. Videos, webinars and podcasts only need the link — Investi builds the player.',
                'Media coverage points at someone else’s story and always opens the publisher’s site in a new tab.',
                'Publishing creates a genuine WordPress post, so it keeps your theme, your menus and your search ranking.',
                'Edit it here later and the post updates itself. If the same field is changed in both places, Investi wins.',
            ],
            tip: 'Tip · “Preview on the Website” opens the real page in a new tab, not an Investi mock‑up, so you see the live theme.',
        },
        {
            target: 'hub-content-board-management', placement: 'right', title: 'Directors, management, secretary',
            body: 'Everyone on your board and management page, in the order visitors see them.',
            points: [
                'The Category — Director, Management or Company Secretary — decides the group on the website and which embed code includes that person.',
                'Each person becomes their own post with the biography on it. It is a separate post type, so people never appear in your news feed.',
                'Photographs are square and should be at least 1000 × 1000 pixels, because the website shows them large.',
                'Drag a card to reorder. The order here is the order on the website, so the Chair can sit first.',
            ],
            tip: 'Tip · The company secretary sits inside Management with a label, rather than in a group of their own.',
        },
        {
            // Figma: "on this page" (the step highlights the card); "Version history keeps every earlier file" — no history list
            target: 'hub-content-corporate-governance', placement: 'left', title: 'Charters, policies and the statement',
            body: 'Two kinds of thing live in this library: the annual governance statement, and the documents behind it.',
            points: [
                'The Corporate Governance Statement has its own field at the top, with the approved date and financial year, because it usually sits above everything else.',
                'Every other document is filed as a Charter or a Policy & Procedure — the only two categories the widget uses.',
                'Replacing a file keeps the same web address, so old bookmarks, emails and Google results still work.',
                'Each document shows who uploaded the current version, and when.',
            ],
            tip: 'Tip · The Appendix 4G is a document like any other — upload it into the list under Policies & Procedures.',
        },
        {
            target: 'hub-content-other-documents', placement: 'right', title: 'A library for everything else',
            body: 'Anything that is not governance: ESG reports, community newsletters, approvals, annual reviews.',
            points: [
                'Folders are yours to name. Create as many as you need, and drag documents to reorder inside one.',
                'The Documents widget shows one folder at a time, so each folder can live on a different page of the website.',
                'A short description under the file name helps visitors pick the right document without opening three PDFs.',
                'Hide a document without deleting it when something is superseded but you want to keep the file.',
            ],
            tip: 'Tip · Replacing a file keeps its link, so a newsletter archive can be corrected without breaking anything.',
        },
        {
            // Figma lists auditor, solicitors and bankers too; the page has no fields for them
            target: 'hub-content-company-details', placement: 'right', title: 'Details used all over the site',
            body: 'One record of who you are and how to reach you, reused everywhere it is needed.',
            points: [
                'Registered and postal address, phone, email, website, ACN and ABN, share registry and social links.',
                'The Corporate directory widget shows the whole table with a single code.',
                'Any one detail also has its own small code — a phone number in the footer, the ACN in the fine print, the LinkedIn link in the header.',
                'Change it once here and every place it appears updates, including email footers and announcement emails.',
            ],
            tip: 'Tip · Leave a field blank and it simply disappears from the table, rather than showing an empty row.',
        },
        {
            target: 'hub-content-capital-structure', placement: 'left', title: 'Numbers, and where they come from',
            body: 'Three groups of figures, and only one of them needs you.',
            points: [
                'From Automic, locked: shares on issue, number of holders, the top 20 percentage and holders below a marketable parcel. Synced every evening.',
                'From the ASX feed, locked: share price, market capitalisation, the 52‑week range and today’s volume, on a 20‑minute delay.',
                'Yours to type: unlisted options, performance rights, escrowed shares, cash at bank, the quarter end and the footnote.',
                'Update history records every change — who made it, when, and what the figure was before.',
            ],
            tip: 'Tip · Update your figures after an Appendix 3B, an Appendix 2A or a quarterly cash flow report, and the widget follows straight away.',
        },
    ],
};

// Contact forms tour step 1..2 (718:34181, 719:33247). Step 2 is drawn on Form Settings; here it highlights the button.
const CONTACT_FORMS: Tour = {
    id: 'contact-forms',
    path: '/website-hub/contact-forms',
    match: hubPage('/website-hub/contact-forms'),
    title: 'Show me around Contact forms',
    meta: 'Reading messages and setting up the forms',
    length: '2 steps · 1 min',
    steps: [
        {
            // Figma: "Assign a message to a colleague"; there is no assigning, so the filters and export instead
            target: 'hub-contact-messages', placement: 'bottom', title: 'Messages from the website',
            body: 'Every enquiry sent through a form on your site arrives here, with context beside it.',
            points: [
                'If the sender is already a contact or a shareholder, Investi shows their history next to the message.',
                'Reply from here and the reply is logged against the contact, so the next person can see what was said.',
                'Filter by status or form, and export the list when you need a record.',
                'Anything caught by the spam filter is held in a review folder for 30 days rather than deleted.',
            ],
            tip: 'Tip · The “are you a shareholder?” question on the form is what lets Investi match a sender to the share register.',
        },
        {
            target: 'hub-contact-form-settings', placement: 'bottom', title: 'Set the forms up once',
            body: '“Form Settings” is split into five tabs so nothing important is buried at the bottom of a long page.',
            points: [
                'Notifications — who is emailed, whether it also shows in Investi, and one email per message or a daily summary.',
                'Automatic reply — the message the sender gets back straight away. It is a normal email template, so it carries your branding.',
                'Questions on the form — drag to reorder, toggle to hide, add your own. Full name, email, subject and message are required.',
                'Consent & after send, and Spam protection — the opt‑in tick‑box, the thank‑you message, and eight layers of filtering.',
            ],
            tip: 'Tip · Change a question here and the form on the website updates itself. There is no new code to paste.',
        },
    ],
};

export const TOUR_REGISTRY: Tour[] = [DASHBOARD, ANALYTICS, REGISTRY_ALL_HOLDERS, REGISTRY_TOP_HOLDERS, REGISTRY_HOLDINGS_MOVEMENT, REGISTRY_REPORTS, REGISTRY_HOLDER, WEBSITE_HUB, WEBSITE_CONTENT, CONTACT_FORMS];

export const getTour = (id: string) => TOUR_REGISTRY.find(t => t.id === id);

export const tourForPath = (pathname: string) => TOUR_REGISTRY.find(t => t.match(pathname));
